/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
/**
 * v77.99.5 — Admin audit log dedicated page.
 *
 * Phase 5 #10. The Operations Console already has a small "Admin
 * audit log" panel showing the most recent 25 entries; this is the
 * full-page version with proper filters, pagination, and click-row-
 * to-expand metadata. Backed by the existing
 * `GET /admin/audit-log` endpoint which already accepts
 * `action / target_id / admin_id / limit` query params.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, RefreshCw, ChevronLeft } from "lucide-react";

const ACTION_OPTIONS = [
  "all", "impersonate_start", "impersonate_stop",
  "premium_grant", "premium_revoke",
  "delete_user", "archive", "restore",
  "gdpr_archive", "gdpr_restore", "export_data",
  "resend_email", "feature_flag_update",
  "cancel_deletion",
];

const PAGE_SIZE = 20;

export default function AdminAuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState("all");
  const [adminId, setAdminId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [page, setPage] = useState(0);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (action !== "all") params.set("action", action);
      if (adminId.trim()) params.set("admin_id", adminId.trim());
      if (targetId.trim()) params.set("target_id", targetId.trim());
      params.set("limit", "500");
      const { data } = await api.get(`/admin/audit-log?${params.toString()}`);
      setEntries(data.entries || data.rows || data || []);
      setPage(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load audit log");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  const pageEntries = useMemo(
    () => entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [entries, page],
  );

  return (
    <div className="px-4 sm:px-6 py-6 max-w-5xl mx-auto" data-testid="admin-audit-log-page">
      <Link
        to="/app/admin"
        className="inline-flex items-center gap-1 text-[12px] text-[#6F828A] hover:text-[#2E3A3F] mb-4"
        data-testid="audit-log-back"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back to admin
      </Link>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
        <div>
          <h1 className="text-[#2E3A3F] text-[22px] font-semibold tracking-tight">Admin audit log</h1>
          <p className="text-[12.5px] text-[#6F828A] mt-1">
            Every administrative action is recorded here. Newest first. {entries.length} matching entries.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          disabled={busy}
          className="h-9 text-[13px] rounded-sm border-[#D9E1E3]"
          data-testid="audit-log-reload"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
          Reload
        </Button>
      </div>

      <div className="rounded-md border border-[#D9E1E3] bg-white p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Action</span>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="h-9 text-[13px] border border-[#D9E1E3] rounded-sm px-2 w-full"
              data-testid="audit-log-filter-action"
            >
              {ACTION_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Admin ID</span>
            <Input
              value={adminId}
              onChange={(e) => setAdminId(e.target.value)}
              placeholder="UUID"
              className="h-9 text-[13px]"
              data-testid="audit-log-filter-admin"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Target ID</span>
            <Input
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="user/household/etc."
              className="h-9 text-[13px]"
              data-testid="audit-log-filter-target"
            />
          </label>
        </div>
        <div className="flex justify-end mt-3">
          <Button
            onClick={load}
            disabled={busy}
            className="h-9 text-[13px] bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm"
            data-testid="audit-log-apply-filters"
          >
            Apply filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#D9E1E3] bg-white" data-testid="audit-log-list">
        {busy ? (
          <div className="text-sm text-[#6F828A] py-12 text-center">Loading…</div>
        ) : pageEntries.length === 0 ? (
          <div className="text-sm text-[#6F828A] py-12 text-center">No audit entries match these filters.</div>
        ) : (
          <ul className="divide-y divide-[#E2E8EC]">
            {pageEntries.map((entry, idx) => (
              <AuditRow key={entry.id || `${entry.ts}-${idx}`} entry={entry} />
            ))}
          </ul>
        )}
      </div>

      {entries.length > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4" data-testid="audit-log-pagination">
          <span className="text-[12px] text-[#6F828A]">Page {page + 1} of {totalPages}</span>
          <div className="flex gap-2">
            <Button
              variant="outline" onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="h-8 text-[12px] rounded-sm"
              data-testid="audit-log-prev"
            >Previous</Button>
            <Button
              variant="outline" onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="h-8 text-[12px] rounded-sm"
              data-testid="audit-log-next"
            >Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry }) {
  const [open, setOpen] = useState(false);
  const hasMeta = entry.meta && Object.keys(entry.meta).length > 0;
  const action = entry.action || "—";
  const targetLabel = entry.target_label || entry.target_email
    || entry.target_id || entry.target_type || "—";
  const result = entry.result || "success";
  const resultTone = result === "success"
    ? "bg-[#E5F1EA] text-[#1E5A1E] border-[#A9D3A9]"
    : "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]";
  return (
    <li className="px-4 py-3" data-testid="audit-log-row">
      <button
        type="button"
        onClick={() => hasMeta && setOpen(!open)}
        className={`w-full text-left flex items-baseline gap-2 ${hasMeta ? "cursor-pointer" : "cursor-default"}`}
      >
        <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border bg-[#ECE3F3] text-[#4E3877] border-[#C5B2DE]">
          {action}
        </span>
        <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border ${resultTone}`}>
          {result}
        </span>
        <span className="text-[12.5px] text-[#2E3A3F] flex-1 truncate">
          <span className="text-[#6F828A]">{entry.admin_email || entry.admin_id || "admin"}</span>
          {" → "}
          <span className="font-medium">{targetLabel}</span>
        </span>
        <span className="text-[10px] text-[#6F828A] font-mono-data">{formatDT(entry.ts || entry.created_at)}</span>
        {hasMeta && (open ? <ChevronUp className="w-3.5 h-3.5 text-[#6F828A]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#6F828A]" />)}
      </button>
      {hasMeta && open && (
        <pre className="mt-2 text-[11px] bg-[#F8FAFB] border border-[#E2E8EC] rounded-sm p-2 overflow-x-auto text-[#2E3A3F]">
          {JSON.stringify(entry.meta, null, 2)}
        </pre>
      )}
    </li>
  );
}
